/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Codi residu */
  codi_residu?: string;
  /** Codi fraccio */
  codi_fraccio?: string;
  /** Categoria */
  id?: string;
  /** Sinonim */
  sinonim?: boolean;
  /** Lang */
  lang?: LangListParamsEnum;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export type LangListParamsEnum =
  'ca' |
  'es' |
  'en';

export interface CreateParams {
  data: __model.Residu;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportFractionResidueRelationshipParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportResidueCategoriesParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportResidueSynonymsParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportResidusParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface GetTaskStatusParams {
  data: __model.TaskId;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportFractionResidueRelationshipParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportResidueCategoriesParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportResidusParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportSynonymsParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface KillTaskParams {
  data: __model.TaskId;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this residu. */
  codi_residu: string;
}

export interface UpdateParams {
  data: __model.Residu;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this residu. */
  codi_residu: string;
}

export interface PartialUpdateParams {
  data: __model.Residu;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this residu. */
  codi_residu: string;
}

export interface DeleteParams {
  /** A unique value identifying this residu. */
  codi_residu: string;
}

export interface DeleteImageParams {
  /** A unique value identifying this residu. */
  codi_residu: string;
}

export interface UploadImageParams {
  /** Image file to upload */
  image: File;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this residu. */
  codi_residu: string;
}

@Injectable()
export class ResiduService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  list(params: ListParams, multipart = false): Observable<__model.ResiduList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      codi_residu: params.codi_residu,
      codi_fraccio: params.codi_fraccio,
      id: params.id,
      sinonim: params.sinonim,
      lang: params.lang,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.ResiduList>(this.apiConfigService.options.apiUrl + `/residu/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.Residu> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Endpoint to export residues' fractions to CSV */
  exportFractionResidueRelationship(params: ExportFractionResidueRelationshipParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/residu/export_fraction_residue_relationship/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to export residue's categories to CSV */
  exportResidueCategories(params: ExportResidueCategoriesParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/residu/export_residue_categories/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to export residue's synonyms to CSV */
  exportResidueSynonyms(params: ExportResidueSynonymsParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/residu/export_residue_synonyms/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to export residus to CSV */
  exportResidus(params: ExportResidusParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/residu/export_residus/`, {params: queryParams, responseType: 'text'});
  }

  getTaskStatus(params: GetTaskStatusParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/residu/get_task_status/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import residues' fractions from CSV */
  importFractionResidueRelationship(params: ImportFractionResidueRelationshipParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/residu/import_fraction_residue_relationship/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import residues' categories from CSV */
  importResidueCategories(params: ImportResidueCategoriesParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/residu/import_residue_categories/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /**
   * Endpoint to import residues from CSV with secondary information support.
   * This endpoint handles the complete residue import including the ManyToMany relationship
   * with InformacioSecundaria from columns 'Informació secundaria 1' through 'Informació secundaria 7'.
   */
  importResidus(params: ImportResidusParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/residu/import_residus/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import synonyms from CSV */
  importSynonyms(params: ImportSynonymsParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/residu/import_synonyms/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  killTask(params: KillTaskParams, multipart = false): Observable<__model.TaskId> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.TaskId>(this.apiConfigService.options.apiUrl + `/residu/kill_task/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.Residu> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.get<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.Residu> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.put<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.Residu> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.patch<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/`, {responseType: 'text'});
  }

  /** Delete the residue photo image */
  deleteImage(params: DeleteImageParams, multipart = false): Observable<__model.Residu> {
    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.delete<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/delete_image/`);
  }

  /** Upload an image for the residue photo (fotografia field) */
  uploadImage(params: UploadImageParams, multipart = false): Observable<__model.Residu> {
    const formDataParams = {
      image: params.image,
    };
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_residu: params.codi_residu,
    };
    return this.http.post<__model.Residu>(this.apiConfigService.options.apiUrl + `/residu/${pathParams.codi_residu}/upload_image/`, formDataParams, {params: queryParams});
  }
}
